import { KeyboardEvent, useEffect, useMemo, useState } from 'react';

import { DndContext, DragEndEvent, DragStartEvent, UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AnimatePresence, AnimationProps, motion } from 'framer-motion';
import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { PortalDragOverlay } from '@components/drag-and-drop';
import { FormLabel } from '@components/form-label';
import { FieldId, FieldOption } from '@components/form/v2';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconDrag } from '@components/icons/drag';
import { IconEdit } from '@components/icons/edit';
import { IconList } from '@components/icons/list';
import { IconRemove } from '@components/icons/remove';
import { IconSettings } from '@components/icons/settings';
import { Select } from '@components/select';
import { ListOptionLabel } from '@components/select/list-option-label';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { SwitchGroup } from '@components/switch';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { Keys } from '@components/types/keyboard';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { randomId } from '@utils/strings';
import { transitionStandard } from '@utils/transitions';

import { useFormBuilderContext } from './context';
import { FieldContainingOptions } from './state';

const SortableOption = ({ id, children }: { id: string; children(props: any): any }) => {
  const sortableProps = useSortable({
    id: id,
  });

  const style = {
    transform: CSS.Transform.toString(sortableProps.transform),
    transition: sortableProps.transition,
    boxShadow: sortableProps.isDragging ? theme.shadow.medium : 'none',
    cursor: 'grab',
    opacity: sortableProps.isDragging ? 0.2 : 1,
  };

  return (
    <div ref={sortableProps.setNodeRef} style={style}>
      {children({
        dragHandle: (
          <IconButton
            variant="ghost-purple"
            icon={<IconDrag />}
            {...sortableProps.attributes}
            {...sortableProps.listeners}
          />
        ),
      })}
    </div>
  );
};

const Option = ({
  option,
  isEditing,
  startEditing,
  finishEditing,
  onKeyDown,
  onEdit,
  onRemove,
  dragHandle,
}: {
  option: FieldOption;
  isEditing: boolean;
  startEditing?(): void;
  finishEditing?(): void;
  onKeyDown?(event: KeyboardEvent<HTMLInputElement>): void;
  onEdit?(value: string): void;
  onRemove?(): void;
  dragHandle?: any;
}) => {
  return (
    <Card space={0}>
      <Stack space={2} orientation="horizontal">
        <Stack
          space={2}
          orientation="horizontal"
          align="center"
          style={{ flexGrow: 1, minWidth: 0 }}
        >
          {dragHandle}
          {isEditing ? (
            <EditingText>
              <TextField
                hideLabel
                label="label"
                autoFocus
                onBlur={finishEditing}
                defaultValue={option.label}
                onChange={onEdit}
                onKeyDown={onKeyDown}
                maxChars={255}
              />
            </EditingText>
          ) : (
            <Text font="regular14" text={option.label} ellipsis />
          )}
        </Stack>

        <Stack space={2} orientation="horizontal">
          <IconButton onClick={startEditing} variant="ghost-purple" icon={<IconEdit />} />
          <IconButton onClick={onRemove} variant="ghost-purple" icon={<IconRemove />} />
        </Stack>
      </Stack>
    </Card>
  );
};

const Options = ({ field }: { field: FieldContainingOptions }) => {
  const [editingId, setEditingId] = useState<FieldId | null>(null);
  const [draggingId, setDraggingId] = useState<UniqueIdentifier | null>(null);
  const {
    store: { actions },
  } = useFormBuilderContext();
  const { t } = useOptionsTranslate();

  const removeOption = (value: string) => {
    actions.updateFieldOptions(field.id, (state) => {
      state.options = state.options.filter((opt) => opt.value !== value);
    });
  };

  const addOption = () => {
    const id = randomId();

    actions.updateFieldOptions(field.id, (state) => {
      state.options.push({ value: id, label: '' });
    });

    setEditingId(id);
  };

  const editOption = (index: number, label: string) => {
    actions.updateFieldOptions(field.id, (state) => {
      state.options[index].label = label;
    });
  };

  const reorder = (fromIndex: number, toIndex: number) => {
    actions.updateFieldOptions(field.id, (state) => {
      const newOpts = state.options.slice();
      newOpts.splice(toIndex, 0, newOpts.splice(fromIndex, 1)[0]);
      state.options = newOpts;
    });
  };

  const draggingOption = draggingId
    ? field.options.find((opt) => opt.value === draggingId)
    : undefined;

  const handleDragEnd = (event: DragEndEvent) => {
    if (!event.active.data.current?.sortable || !event.over?.data.current?.sortable) {
      return;
    }
    reorder(event.active.data.current.sortable.index, event.over.data.current.sortable.index);
  };

  const handleDragStart = (event: DragStartEvent) => setDraggingId(event.active.id);
  const handleDragCancel = () => setDraggingId(null);

  useEffect(() => {
    if (!editingId) {
      actions.updateFieldOptions(field.id, (state) => {
        state.options = state.options.filter((opt) => opt.label !== '');
      });
    }
  }, [actions, editingId, field.id]);

  return (
    <DndContext
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
    >
      <PortalDragOverlay>
        {draggingOption && (
          <OverlayContainer>
            <Option
              option={draggingOption}
              isEditing={false}
              dragHandle={<IconButton variant="ghost-purple" icon={<IconDrag />} />}
            />
          </OverlayContainer>
        )}
      </PortalDragOverlay>
      <Stack space={1}>
        <FormLabel text={t('title')} />

        <AnimatePresence initial={false}>
          {field.options.length === 0 && (
            <motion.div
              animate={{ height: 'auto', opacity: 1, y: 0 }}
              initial={{ height: 0, opacity: 0, y: -5 }}
              transition={{ type: 'just' }}
            >
              <Card variant="gray" space={6}>
                <Stack space={4} orientation="horizontal">
                  <IconSettings />
                  <Text text={t('noOptions')} color="textSecondary" />
                </Stack>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        <SortableContext
          items={field.options.map((opt) => opt.value)}
          strategy={verticalListSortingStrategy}
        >
          <AnimatePresence initial={false}>
            {field.options.map((option, index) => (
              <motion.div key={option.value} {...optionAnimation}>
                <SortableOption id={option.value} key={option.value}>
                  {({ dragHandle }) => (
                    <Option
                      dragHandle={dragHandle}
                      option={option}
                      isEditing={option.value === editingId}
                      startEditing={() => setEditingId(option.value)}
                      finishEditing={() => setEditingId(null)}
                      onEdit={(value) => editOption(index, value)}
                      onRemove={() => removeOption(option.value)}
                      onKeyDown={(event) => {
                        if (event.key === Keys.Enter) {
                          if (index === field.options.length - 1) {
                            // If we press enter on the last option, add a new empty one
                            addOption();
                          } else {
                            // Pressing enter goes to edit the next option in the list
                            setEditingId(field.options[index + 1].value);
                          }
                        } else if (event.key === Keys.Escape) {
                          event.stopPropagation();
                          event.preventDefault();
                          setEditingId(null);

                          if (!option.label) {
                            // Pressing ESC on an empty editing box removes that option
                            removeOption(option.value);
                          }
                        }
                      }}
                    />
                  )}
                </SortableOption>
              </motion.div>
            ))}
          </AnimatePresence>
        </SortableContext>
      </Stack>
      <Stack orientation="horizontal" justify="flex-end">
        <Button
          variant="secondary"
          iconLeft={<IconAddPlus />}
          text={t('addOption')}
          onClick={addOption}
        />
      </Stack>
    </DndContext>
  );
};

const useOptionsTranslate = () => useScopedTranslate(optionsLocale);

const optionsLocale = {
  en: {
    title: 'Options',
    addOption: 'Add option',
    selectPredefined: 'Pre-defined list options',
    noOptions: 'No options added',
    listOptions: 'List options',
    label: 'Label',
    value: 'Value',
    syncedList: 'Synced list',
    showHideAll: 'Show/Hide All',
  },
};

const OptionsContent = ({ field }: { field: FieldContainingOptions }) => {
  const { t } = useOptionsTranslate();
  const {
    predefinedOptionsLists: predefinedLists,
    onJourneyAttributeListChange,
    store: { actions },
  } = useFormBuilderContext();

  const [initialListId] = useState(() => {
    if (!field.optionsFromListId) return null;
    const list = predefinedLists.find((l) => l.value === field.optionsFromListId);
    if (list?.isHidden) return list.value;
    return null;
  });

  const selectedList = field.optionsFromListId
    ? predefinedLists.find((l) => l.value === field.optionsFromListId && !l.isHidden)
    : null;

  const selectableLists = useMemo(() => {
    return predefinedLists
      .filter((list) => !list.isHidden)
      .map((list) => ({
        value: list.value,
        filterText: list.label,
        label: (
          <ListOptionLabel
            name={list.label}
            isSynced={list.listType === 'synced'}
            isSyncActive={list.isSyncActive}
            tooltip={t('syncedList')}
          />
        ),
      }));
  }, [predefinedLists, t]);

  const selectPredefinedList = (listId: string) => {
    if (listId === '' && initialListId) {
      const list = predefinedLists.find((l) => l.value === initialListId);
      actions.createChangeHandler(field, 'optionsFromListId')(initialListId);
      actions.createChangeHandler(field, 'options')(list?.options ?? []);
      if ('journeyAttributeId' in field && field.journeyAttributeId) {
        onJourneyAttributeListChange?.(field.journeyAttributeId, initialListId);
      }
      return;
    }
    actions.createChangeHandler(
      field,
      'defaultValue'
    )(field.kind === 'input-multiple-choice' ? [] : null);
    actions.createChangeHandler(field, 'optionsFromListId')(listId || null);
    actions.createChangeHandler(field, 'options')([]);
    actions.createChangeHandler(field, 'excludedOptions')([]);
    if ('journeyAttributeId' in field && field.journeyAttributeId) {
      onJourneyAttributeListChange?.(field.journeyAttributeId, listId || null);
    }
  };

  return (
    <Container>
      <Collapse isCollapsed={Boolean(selectedList)}>
        <Stack space={6}>
          <Options field={field} />
          {selectableLists && (
            <Stack space={4} orientation="horizontal" style={{ width: '100%' }} align="center">
              <Separator />
              <Text font="semibold14" caps color="purple60" text={t('core.or')} />
              <Separator />
            </Stack>
          )}
        </Stack>
      </Collapse>
      {selectableLists && (
        <Stack space={4}>
          <Select
            options={selectableLists}
            label={t('selectPredefined')}
            onChange={selectPredefinedList}
            defaultValue={field.optionsFromListId ?? undefined}
            hasFilterText
            optionContentFullWidth
          />
          {selectedList && (
            <Stack space={2}>
              <Text font="semibold14" text={t('listOptions')} />

              <SwitchGroup
                label={t('listOptions')}
                hideLabel
                toggleAllLabel={t('showHideAll')}
                options={selectedList.options}
                value={selectedList.options
                  .map((option) => option.value)
                  .filter((value) => !field.excludedOptions?.includes(value))}
                onChange={(values) => {
                  const excluded = selectedList.options
                    .map((option) => option.value)
                    .filter((value) => !values.includes(value));
                  actions.createChangeHandler(field, 'excludedOptions')(excluded);

                  const isMulti = field.kind === 'input-multiple-choice';
                  const currentDefaults = (
                    isMulti ? [...field.defaultValue] : [field.defaultValue]
                  ) as string[];
                  const filtered = currentDefaults.filter((value) => !excluded.includes(value));
                  actions.createChangeHandler(
                    field,
                    'defaultValue'
                  )(isMulti ? filtered : filtered[0] ?? '');
                }}
              />
            </Stack>
          )}
        </Stack>
      )}
    </Container>
  );
};

export const optionsTab = (field: FieldContainingOptions) => {
  const { t } = getScopedTranslate(optionsLocale);

  return {
    label: t('title'),
    icon: IconList,
    content: <OptionsContent field={field} />,
  };
};

const EditingText = styled.div({
  marginLeft: pxToRem(-16),
});

const Container = styled(Stack).attrs({ space: 6 })({
  padding: pxToRem(24),
});

const optionAnimation: AnimationProps = {
  initial: { height: 0, opacity: 0, overflow: 'hidden' },
  exit: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  transition: { duration: 0.15 },
};

const OverlayContainer = styled.div({
  boxShadow: theme.shadow.medium,
  transform: 'scale(0.99)',
  transition: transitionStandard('all'),
});
